import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiX, FiCheck } from 'react-icons/fi';
import { categoriaService } from '../services/onebaseService';
import { toast } from 'react-toastify';
import './MaestroCategoriaPresupuesto.css';

import Loading from '../components/Loading';
const MaestroCategoriaPresupuesto = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    orden: 0,
    status: 'activo',
  });

  useEffect(() => {
    cargarCategorias();
  }, []);

  const cargarCategorias = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoriaService.getAll({ status: 'activo' });

      // Manejar diferentes formatos de respuesta
      let categoriasArray = [];
      if (Array.isArray(response.data)) {
        categoriasArray = response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        categoriasArray = response.data.results;
      }

      // Ordenar por orden
      categoriasArray.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      setCategorias(categoriasArray);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
      setError('Error al cargar las categorías');
      toast.error('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirModal = (categoria = null) => {
    if (categoria) {
      setEditando(categoria);
      setFormData({
        nombre: categoria.nombre,
        descripcion: categoria.descripcion || '',
        orden: categoria.orden || 0,
        status: categoria.status || 'activo',
      });
    } else {
      setEditando(null);
      setFormData({
        nombre: '',
        descripcion: '',
        orden: categorias.length + 1,
        status: 'activo',
      });
    }
    setShowModal(true);
  };

  const handleCerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setFormData({
      nombre: '',
      descripcion: '',
      orden: 0,
      status: 'activo',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'orden' ? parseInt(value) : value,
    }));
  };

  const handleGuardar = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      setLoading(true);
      if (editando) {
        await categoriaService.update(editando.id, formData);
        toast.success('Categoría actualizada correctamente');
      } else {
        await categoriaService.create(formData);
        toast.success('Categoría creada correctamente');
      }
      handleCerrarModal();
      await cargarCategorias();
    } catch (err) {
      console.error('Error al guardar categoría:', err);
      toast.error(editando ? 'Error al actualizar la categoría' : 'Error al crear la categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (categoria) => {
    if (!window.confirm(`¿Está seguro de eliminar la categoría "${categoria.nombre}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await categoriaService.delete(categoria.id);
      toast.success('Categoría eliminada correctamente');
      await cargarCategorias();
    } catch (err) {
      console.error('Error al eliminar categoría:', err);
      toast.error('Error al eliminar la categoría');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar categorías según búsqueda
  const categoriasFiltradas = categorias.filter(categoria =>
    categoria.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (categoria.descripcion && categoria.descripcion.toLowerCase().includes(busqueda.toLowerCase()))
  );

  if (loading && categorias.length === 0) {
    return (
      <div className="maestro-categoria-container">
        <Loading fullScreen={false} mensaje="Cargando categorías" />
      </div>
    );
  }

  return (
    <div className="maestro-categoria-container">
      {/* Header */}
      <div className="maestro-categoria-header">
        <div>
          <h1>📂 Maestro de Categorías de Presupuesto</h1>
          <p>Gestiona las categorías disponibles para clasificar los items de presupuestos</p>
        </div>
        <button
          className="estado-btn"
          onClick={() => handleAbrirModal()}
          disabled={loading}
        >
          <FiPlus /> Nueva Categoría
        </button>
      </div>

      {/* Buscador */}
      <div className="maestro-categoria-search">
        <input
          type="text"
          placeholder="🔍 Buscar categoría por nombre o descripción..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Tabla de Categorías */}
      <div className="maestro-categoria-table-container">
        {categoriasFiltradas.length > 0 ? (
          <table className="maestro-categoria-table">
            <thead>
              <tr>
                <th>Orden</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {categoriasFiltradas.map((categoria) => (
                <tr key={categoria.id}>
                  <td className="orden">{categoria.orden || '-'}</td>
                  <td className="nombre">{categoria.nombre}</td>
                  <td className="descripcion">{categoria.descripcion || '-'}</td>
                  <td className="estado">
                    <span className={`estado-badge ${categoria.status}`}>
                      {categoria.status === 'activo' ? '✓ Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="acciones">
                    <button
                      className="btn-icon"
                      onClick={() => handleAbrirModal(categoria)}
                      title="Editar"
                      disabled={loading}
                    >
                      <FiEdit />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => handleEliminar(categoria)}
                      title="Eliminar"
                      disabled={loading}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay categorías disponibles</p>
          </div>
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editando ? 'Editar Categoría' : 'Nueva Categoría'}</h2>
              <button
                className="close-btn"
                onClick={handleCerrarModal}
                disabled={loading}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleGuardar} className="modal-form">
              <div className="form-group">
                <label htmlFor="nombre">Nombre *</label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  placeholder="Ej: Producción, Pre-producción, etc."
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="descripcion">Descripción</label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  placeholder="Describe el propósito de esta categoría..."
                  disabled={loading}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="orden">Orden</label>
                  <input
                    type="number"
                    id="orden"
                    name="orden"
                    value={formData.orden}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status">Estado</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCerrarModal}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : (editando ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaestroCategoriaPresupuesto;
